/**
 * Aiguillage de l'application.
 *
 * Trois états seulement, tant que rien d'autre n'a été conçu :
 *   - on regarde s'il reste une session valable (au rechargement de la page) ;
 *   - sinon, l'écran de connexion ;
 *   - une fois entré, la barre haute et l'écran du module courant.
 *
 * **La barre haute est ici, et non dans les écrans**, depuis le 03/09/2026.
 * Elle l'était dans `EcranAnnuaire`, ce qui tenait tant qu'il n'y avait qu'un
 * écran : le deuxième l'aurait recopiée, et deux barres jumelles auraient
 * divergé au premier changement porté d'un seul côté — la raison même qui a
 * fait paramétrer l'annuaire plutôt que le dédoubler. Chaque écran ne rend plus
 * que son corps.
 *
 * **Le sous-menu appartient au module, pas à la barre.** C'est ce fichier qui
 * sait que le module Annuaire a trois entrées et que ce sont les trois
 * annuaires ouverts ; la barre ne fait que dessiner la liste qu'on lui donne.
 * Un module sans sous-menu n'en dessine pas.
 *
 * Pas de routeur ni d'URL : le module courant et l'annuaire courant sont des
 * états de React, décidé le 03/09/2026 — même raisonnement que pour le reste de
 * l'écran. Conséquence assumée et à reprendre : un rechargement ramène aux
 * clients, et une fiche ne se partage pas par un lien.
 *
 * Le rechargement repasse par `/moi` : un jeton encore valide ne prouve pas que
 * l'accès l'est toujours. Un compte désactivé perd la main au rechargement, sans
 * attendre l'expiration du jeton.
 */

import { useCallback, useEffect, useState } from 'react';
import { lireMoi, type Moi } from './api.js';
import { authentification, jetonCourant, seDeconnecter } from './authentification.js';
import { BarreHaute } from './BarreHaute.js';
import { EcranAnnuaire } from './EcranAnnuaire.js';
import { EcranConnexion } from './EcranConnexion.js';
import { EcranAbsences } from './EcranAbsences.js';
import { EcranAffectation } from './EcranAffectation.js';
import { EcranParc } from './EcranParc.js';
import { EcranOrdres } from './EcranOrdres.js';
import { EcranPlanning } from './EcranPlanning.js';
import { EcranAffretement } from './EcranAffretement.js';
import { EcranLitiges } from './EcranLitiges.js';
import { EcranFinance } from './EcranFinance.js';
import { EcranEmballage } from './EcranEmballage.js';
import { EcranTournees } from './EcranTournees.js';
import { EcranParametres } from './EcranParametres.js';
import { ANNUAIRE_CLIENTS } from './clients.js';
import { ANNUAIRE_TRANSPORTEURS } from './transporteurs.js';
import { ANNUAIRE_SITES } from './sites.js';
import { capitale, type Annuaire } from './annuaire.js';
import { MODULES, type SousMenu } from './modules.js';
import { PARC_VEHICULES, PARCS } from './parcs.js';

/** Les annuaires ouverts, dans l'ordre du sous-menu. */
const ANNUAIRES: readonly Annuaire[] = [
  ANNUAIRE_CLIENTS,
  ANNUAIRE_TRANSPORTEURS,
  ANNUAIRE_SITES,
];

/**
 * Le sous-menu du module Annuaire : un annuaire, une entrée. Il se déduit de la
 * liste ci-dessus plutôt que d'être écrit à côté — sinon ouvrir un quatrième
 * annuaire demanderait de penser à deux endroits.
 */
const SOUS_MENU_ANNUAIRE: readonly SousMenu[] = ANNUAIRES.map((a) => ({
  cle: a.chemin,
  nom: capitale(a.pluriel),
}));

/**
 * Le sous-menu du module Ressources, dans l'ordre de la maquette validée :
 * l'affectation d'abord — c'est ce qu'on regarde tous les matins —, puis les
 * absences, puis les trois parcs, déduits de `PARCS` pour la même raison que
 * ci-dessus.
 */
const SOUS_MENU_RESSOURCES: readonly SousMenu[] = [
  { cle: 'affectation', nom: 'Affectation' },
  { cle: 'absences', nom: 'Absences' },
  ...PARCS.map((p) => ({ cle: p.cle, nom: capitale(p.pluriel) })),
];

/**
 * Le sous-menu du module Exploitation, dans l'ordre de la maquette validée.
 *
 * Le PLANNING ouvre le 04/09/2026, juste après les ordres — c'est l'écran qu'on
 * ouvre le matin en arrivant. Les entrées suivantes du cadrage (recherche de
 * place, optimisation, litiges) ne sont pas écrites tant qu'elles
 * n'existent pas : une porte qui ne mène nulle part ne vaut pas mieux que rien.
 */
const SOUS_MENU_EXPLOITATION: readonly SousMenu[] = [
  { cle: 'ordres', nom: 'Ordres de transport' },
  { cle: 'planning', nom: 'Planning' },
  // La PRÉPARATION ouvre le 05/09/2026, après le planning : on planifie
  // d'abord, on groupe ensuite. L'écran ne crée rien — il regroupe et ordonne
  // des étapes qui existent déjà.
  { cle: 'tournees', nom: 'Préparation' },
  // LES LITIGES ouvrent le 07/09/2026, entre la préparation et l'emballage :
  // c'est l'ordre du cadrage §6, « Ordres de transport, Planification,
  // Litiges, Emballage ». Ils se SAISISSENT sur l'étape, dans la fiche de
  // l'ordre ; cette entrée est la liste de travail — ce qu'il reste à régler,
  // et ce que ça bloque en facturation.
  { cle: 'litiges', nom: 'Litiges' },
  // L'EMBALLAGE ouvre le 06/09/2026. Il ne saisit rien : l'échange se pose sur
  // l'ÉTAPE, dans la fiche de l'ordre — le cadrage §6 est explicite. Cette
  // entrée MONTRE les soldes, qui se calculent des mouvements, et TIENT les
  // bons que les clients nous remettent.
  { cle: 'emballage', nom: 'Emballage' },
  // L'AFFRÈTEMENT ouvre le 06/09/2026, DANS Exploitation — décision du 05/09 :
  // « on enlève le module affrètement, tout est intégré à exploitation ». Il
  // répond à une seule question : qui appeler pour cet axe, avec ce matériel.
  { cle: 'affretement', nom: 'Affrètement' },
];

type Etat =
  | { readonly nom: 'verification' }
  | { readonly nom: 'dehors' }
  | { readonly nom: 'dedans'; readonly moi: Moi };

export function Application() {
  const [etat, setEtat] = useState<Etat>({ nom: 'verification' });
  const [module, setModule] = useState('annuaire');
  const [annuaire, setAnnuaire] = useState<Annuaire>(ANNUAIRE_CLIENTS);
  // Le sous-menu des ressources porte l'affectation ET les trois parcs : une
  // seule clé les distingue, plutôt que deux états qui pourraient diverger.
  const [ressource, setRessource] = useState('affectation');
  const [exploitation, setExploitation] = useState('ordres');
  /**
   * Un ordre que le PLANNING demande d'ouvrir. C'est le seul passage d'un écran
   * à l'autre du logiciel, et il tient en un identifiant : l'application
   * bascule sur les ordres de transport, l'écran des ordres l'ouvre, puis
   * remet ceci à `null` pour qu'un retour à la liste ne le rouvre pas.
   */
  const [ordreAOuvrir, setOrdreAOuvrir] = useState<string | null>(null);

  // Ces deux fonctions descendent dans l'écran des clients, où elles servent de
  // dépendances à un `useEffect`. Sans `useCallback`, elles changeraient
  // d'identité à chaque rendu et l'effet se relancerait sans fin.
  const sortir = useCallback(() => {
    void (async () => {
      await seDeconnecter();
      setEtat({ nom: 'dehors' });
    })();
  }, []);

  const jeton = useCallback(() => jetonCourant(), []);

  useEffect(() => {
    let abandonne = false;

    void (async () => {
      const { data } = await authentification.getSession();
      const session = data.session;
      if (session === null) {
        if (!abandonne) setEtat({ nom: 'dehors' });
        return;
      }
      const acces = await lireMoi(session.access_token);
      if (abandonne) return;
      if (acces.etat === 'ok') {
        setEtat({ nom: 'dedans', moi: acces.moi });
      } else {
        // La session existe encore mais l'accès ne suit plus : on la referme
        // plutôt que de la laisser retrouvée au prochain chargement.
        await seDeconnecter();
        if (!abandonne) setEtat({ nom: 'dehors' });
      }
    })();

    return () => {
      abandonne = true;
    };
  }, []);

  if (etat.nom === 'verification') {
    return (
      <main className="scene">
        <p className="attente">Vérification de la session…</p>
      </main>
    );
  }

  if (etat.nom === 'dehors') {
    return <EcranConnexion quandConnecte={(moi) => setEtat({ nom: 'dedans', moi })} />;
  }

  const societe = etat.moi.societes[0];

  return (
    <div className="application">
      <BarreHaute
        modules={MODULES}
        module={module}
        quandModule={setModule}
        sousMenu={
          module === 'annuaire'
            ? SOUS_MENU_ANNUAIRE
            : module === 'exploitation'
              ? SOUS_MENU_EXPLOITATION
              : module === 'ressources'
                ? SOUS_MENU_RESSOURCES
                : // AUCUN SOUS-MENU AILLEURS, ET C'EST LE DÉFAUT.
                  //
                  // Il l'est depuis le 07/09/2026, sur constat de l'utilisateur :
                  // le module FINANCE affichait le sous-menu des Ressources —
                  // Affectation, Absences, les trois parcs — qui n'y mènent à
                  // rien. En cause, un défaut par REPLI : tout module non nommé
                  // ci-dessus recevait celui des ressources. Un module qui
                  // s'ouvre sans qu'on y pense héritait donc du menu d'un autre.
                  //
                  // PARAMÈTRES tombe ici aussi, et c'est voulu : ses trois
                  // listes sont des ONGLETS dans l'écran. Elles se lisent
                  // d'affilée, et une barre de plus au-dessus d'un tableau de
                  // quatre lignes ferait deux niveaux pour rien.
                  //
                  // FINANCE aura les siens — le cadrage de l'étape 10 en prévoit
                  // cinq : Facturation, Recouvrement, Analyse, Ressources,
                  // Paramètres. Tant qu'un seul écran existe, il n'y a rien à
                  // choisir, et une barre à une entrée ne dit rien.
                  []
        }
        sousMenuCourant={
          module === 'annuaire'
            ? annuaire.chemin
            : module === 'exploitation'
              ? exploitation
              : ressource
        }
        quandSousMenu={(cle) => {
          if (module === 'annuaire') {
            const choisi = ANNUAIRES.find((a) => a.chemin === cle);
            if (choisi !== undefined) setAnnuaire(choisi);
            return;
          }
          if (module === 'exploitation') {
            setExploitation(cle);
            return;
          }
          setRessource(cle);
        }}
        societe={societe?.raison_sociale ?? 'Société inconnue'}
        utilisateur={etat.moi.utilisateur}
        quandDeconnexion={sortir}
      />
      {module === 'finance' ? (
        <EcranFinance jeton={jeton} quandAccesPerdu={sortir} />
      ) : module === 'parametres' ? (
        <EcranParametres jeton={jeton} quandAccesPerdu={sortir} />
      ) : module === 'annuaire' ? (
        <EcranAnnuaire annuaire={annuaire} jeton={jeton} quandAccesPerdu={sortir} />
      ) : module === 'exploitation' ? (
        exploitation === 'litiges' ? (
          <EcranLitiges
            jeton={jeton}
            quandAccesPerdu={sortir}
            quandOuvrirOrdre={(otId) => {
              setOrdreAOuvrir(otId);
              setExploitation('ordres');
            }}
          />
        ) : exploitation === 'affretement' ? (
          <EcranAffretement
            jeton={jeton}
            quandAccesPerdu={sortir}
            quandOuvrirOrdre={(otId) => {
              setOrdreAOuvrir(otId);
              setExploitation('ordres');
            }}
          />
        ) : exploitation === 'emballage' ? (
          <EcranEmballage jeton={jeton} quandAccesPerdu={sortir} />
        ) : exploitation === 'tournees' ? (
          <EcranTournees jeton={jeton} quandAccesPerdu={sortir} />
        ) : exploitation === 'planning' ? (
          <EcranPlanning
            jeton={jeton}
            quandAccesPerdu={sortir}
            quandOuvrirOrdre={(otId) => {
              setOrdreAOuvrir(otId);
              setExploitation('ordres');
            }}
          />
        ) : (
          <EcranOrdres
            jeton={jeton}
            quandAccesPerdu={sortir}
            ouvrirOrdre={ordreAOuvrir}
            quandOrdreOuvert={() => setOrdreAOuvrir(null)}
          />
        )
      ) : ressource === 'affectation' ? (
        <EcranAffectation jeton={jeton} quandAccesPerdu={sortir} />
      ) : ressource === 'absences' ? (
        <EcranAbsences jeton={jeton} quandAccesPerdu={sortir} />
      ) : (
        <EcranParc
          parc={PARCS.find((p) => p.cle === ressource) ?? PARC_VEHICULES}
          jeton={jeton}
          quandAccesPerdu={sortir}
        />
      )}
    </div>
  );
}
